import { faGithub, faInstagram, faLinkedin } from "@fortawesome/free-brands-svg-icons";

import { PERSON_NAME, SOCIAL } from "./identity";

/** Page copy and career facts that are not translated (identity facts come from ./identity). */
const INFO = {
	main: {
		title: PERSON_NAME,
		name: PERSON_NAME,
		tagline_homepage:
			"Je construis à l'intersection de l'Art, de l'Intelligence et de l'Industrie.",
		tagline_hero: "I don't just train models. I engineer companies.",
		subtitle: "Audio AI · LLM Systems · Research to Production",
		roles: ["CTO", "Entrepreneur", "ML Engineer", "System Architect", "Indie Maker"],
		profileImage: "profil.jpg",
	},

	socials: {
		github: {
			link: SOCIAL.github,
			icon: faGithub,
		},
		linkedin: {
			link: SOCIAL.linkedin,
			icon: faLinkedin,
		},
		instagram: {
			link: SOCIAL.instagram,
			icon: faInstagram,
		},
		malt: {
			link: SOCIAL.malt,
		},
		// No email here: see ./email.js (never ship it in plain text).
	},

	manifesto: {
		title: "About Me",
		subtitle: "CTO • Entrepreneur • Machine Learning Engineer",
		quote: "Je ne construis pas seulement du code, je construis des systèmes qui durent.",
		principles: [
			"L'architecture avant le code",
			"La vision avant l'exécution",
			"La simplicité comme arme absolue",
			"L'impact comme seule métrique",
		],
	},

	modules: {
		startups: {
			title: "VENTURES",
			subtitle: "Entreprises • Scalabilité • Innovation",
			description: "Mes entreprises et projets entrepreneuriaux en forte croissance.",
			url: "/startups",
			icon: "cpu",
			ventures: [
				{
					name: "2am",
					role: "CTO & Co-Founder",
					description: "Operational platform for the live industry powered by AI.",
					status: "SCALING_FAST",
					website: "2am-music.com",
					focus: "Audience Intelligence for Live Entertainment",
					responsibilities: [
						"Strategic & Technical Direction",
						"Scalable Cloud Infrastructure",
						"State of the art systems and technologies",
						"R&D Leadership",
					],
				},
				{
					name: "Azerty Labs",
					role: "CEO & Fondatrice",
					description: "Hardware Innovation. From Neural R&D to Production.",
					status: "HARDWARE_DEVELOPMENT",
					website: "azerty-labs.com",
					focus: "Embedded AI & Hardware",
					responsibilities: [
						"End-to-End Product Design",
						"Industrialization & Production",
						"Embedded Audio ML R&D",
						"Go-to-Market Strategy",
					],
				},
			],
		},

		projects: {
			title: "PROJECTS",
			subtitle: "Scalable Systems • ML Infrastructure • Innovation",
			description:
				"Deep dives into complex engineering challenges and architectural breakthroughs.",
			url: "/projects",
			icon: "flask",
			categories: [
				"System Architecture",
				"ML Infrastructure",
				"Audio Intelligence",
				"Deep Tech",
			],
			focus: ["Distributed Systems", "Real-time Inference", "Generative Audio", "Edge AI"],
			projectTemplate: {
				title: "",
				category: "",
				year: "",
				description: "",
				challenge: "",
				approach: "",
				result: "",
				tags: [],
				link: "",
				image: "",
				featured: false,
			},
		},

		journal: {
			title: "JOURNAL",
			subtitle: "Deep Dives • Research • Implementation",
			description:
				"Technical deep-dives into ML systems, paper reviews, and engineering implementations.",
			url: "/journal",
			icon: "book-open",
			tags: ["#MachineLearning", "#Research", "#AudioML", "#DeepDives", "#Papers"],
			entryTemplate: {
				title: "",
				date: "",
				category: "",
				content: "",
				tags: [],
				links: [],
				status: "published", // draft, published
			},
		},

		manifesto: {
			title: "ABOUT ME",
			subtitle: "Philosophie • Parcours • Vision",
			description: "Mon histoire, mes principes et ce qui guide mon travail.",
			url: "/about",
			icon: "feather",
			tags: ["#philosophy", "#values", "#vision"],
		},
	},

	experience: {
		current: {
			title: "Co-Founder & CTO",
			company: "2AM",
			period: "Sept. 2024 – Présent",
			location: "Paris, France · Hybride",
			description: "Building autonomous AI agents for the entertainment industry.",
		},

		previous: [
			{
				title: "AI Machine Learning Engineer",
				company: "Radio France",
				period: "Nov. 2024 – Présent",
				type: "Freelance",
				location: "Paris, France",
				description:
					"As a freelance AI and machine learning engineer, I transform cutting-edge research into practical, real-world solutions. From scientific research to deploying AI products, I design and implement models across diverse domains, including image, video, music, audio processing, and real-time embedded systems, all driven by innovation.",
			},
			{
				title: "Machine Learning AI Engineer - Tech Lead",
				company: "Ircam Amplify",
				period: "Janv. 2022 – Mars 2024 · 2 ans 3 mois",
				type: "CDI",
				location: "Paris, France · Sur site",
				description:
					"As a Tech Lead and Machine Learning Engineer at Ircam Amplify, I leveraged my expertise to transform cutting-edge research from the Ircam laboratory into tangible applications. I supervised scientific and technical developments while guiding the transition from scientific publication to product deployment.",
				keyProjects: [
					"Vocal Separator: Voice extraction in noisy environments",
					"Music Classifier: Musical classification into 139 sub-genres using CNN",
					"SuperVP Lite: Pitch shifting tool with time and transient management",
					"Audio Text Aligner: Alignment of long texts with audio",
					"Speaker Active Control: Reduction of Total Harmonic Distortion (THD)",
					"YouTube Music Scraping: Music extraction via the Google API",
					"Voice Detection Algorithms: Voice classification for audio processing",
				],
			},
			{
				title: "Research into the Cloning of Musical Instruments by Machine Learning",
				company: "Sony CSL Music Team",
				period: "Mars 2020 – Sept. 2020 · 7 mois",
				type: "Stage",
				location: "Paris, France",
				description:
					"During my internship at Sony CSL, I focused on the research, formalization, implementation, and training of audio cloning models for real-time synthesis of musical instruments. Under the guidance of Gaétan Hadjeres, I developed state-of-the-art machine learning models aimed at enhancing audio cloning techniques and engaged in fundamental research on frequency modulation (FM) synthesis.",
			},
			{
				title: "2/3/4G Quality of Service Validation",
				company: "Orange",
				period: "Juil. 2017 – Août 2017 · 2 mois",
				type: "CDD",
				location: "Villejuif, France",
				description:
					"Validation and monitoring of quality of service during the rollout of the French 4G network. Implementation of standardized procedures.",
			},
			{
				title: "Characterization of String Changes on the 'Ruckers-Taskin' Harpsichord",
				company: "Télécom Paris",
				period: "Mai 2016 – Juin 2016 · 2 mois",
				type: "Stage",
				location: "Paris, France",
				description:
					"Analysis of timbre changes following replacement of the strings on the 'Ruckers-Taskin' clavevin (Philharmonie de Paris) using signal processing and Machine Learning.",
			},
			{
				title: "Characterization of Stepper Motors for High-Speed Tomography Equipment",
				company: "Synchrotron SOLEIL",
				period: "Janv. 2015 – Févr. 2015 · 2 mois",
				type: "Stage",
				location: "Gif-sur-Yvette, France",
				description:
					"Statistical study of stepper motors, embedded development for measuring devices.",
			},
			{
				title: "Scientific Popularization of Combustion Phenomena",
				company: "ENSTA ParisTech",
				period: "Juil. 2014 – Juil. 2014 · 1 mois",
				type: "Stage",
				location: "Palaiseau, France",
				description:
					"Study and Popularization of Nitrification Processes and Radical Chemistry.",
			},
		],
	},

	education: {
		main: [
			{
				title: "Master Science et Musique",
				school: "Ircam • Sorbonne Université",
				period: "2019 – 2020",
			},
			{
				title: "Diplôme Ingénieur Généraliste",
				school: "IMT Atlantic",
				period: "2016 – 2021",
			},
		],
	},

	contact: {
		title: "Une idée ? Un projet ?",
		subtitle: "Présence nette, presque cinématographique.",
		description: "Je suis là pour construire, pas pour décorer.",
		availability: "Disponible pour missions freelance",
	},

	footer: {
		version: "v.2025.01",
		host: "GCP-Cloud-Run",
		state: "scaling-fast / functional",
		tech: "React • Tailwind • Deployed with care",
	},

	dieterPrinciples: [
		"Bon design est innovant",
		"Bon design rend un produit utile",
		"Bon design est esthétique",
		"Bon design rend un produit compréhensible",
		"Bon design est discret",
		"Bon design est honnête",
		"Bon design est durable",
		"Bon design est précis jusque dans les moindres détails",
		"Bon design est respectueux de l'environnement",
		"Bon design est minimaliste",
	],
};

export default INFO;
